import AppPage from '@/components/Page/Page'
import { isValidElement, type CSSProperties, type ReactElement, type ReactNode, Suspense } from 'react'
import { Clamp, Columns, Stack } from '@christiankaindl/lyts'
import { Content as ContentClient } from './Content'
import { Header as HeaderClient } from './Header'
import { InfoOverlay, BottomRight, ScrollToTopButton } from './Utils'
import MainLayoutFallback from './MainLayoutFallback'
import Placeholder from '../Placeholder/Placeholder'
import { Desktop, Mobile } from '../MediaQuery/MediaQuery'
import BottomBar from '../Law/BottomBar/BottomBar'
import ClientSidebar from '../Law/Sidebar/Sidebar.client'
import * as sidebarStyles from '@/components/Law/Sidebar/Sidebar.css'
import * as styles from '@/app/[category]/[subCategory]/[norm]/law.css'

export interface PageProps {
  /** Page title */
  title: string
  header: ReactNode
  sidebar?: {
    className?: string
    headerButtons?: ReactElement<any>
    content?: ReactElement<any>
    headerClassName?: string
    forceShowTitle?: boolean
    title?: string
    style?: CSSProperties
    abbreviation?: string
    showSearch?: boolean
  }
  content: ((...props: any) => Promise<ReactElement>) | ReactElement
  /** Content that is shown in the InfoOverlay */
  infoContent?: ReactElement<any>
  /** Content that is displayed in the sidebar (desktop) or the TocOverlay (mobile) */
  tocContent?: ReactElement<any>
  menu?: ReactNode
  search?: boolean
  abbrNr?: number
  abbreviation?: string
  normId: string
  isFallback?: boolean
  clauseId?: string
}
export function Page ({ title, header, sidebar, content: Content, infoContent, tocContent, menu, search = true, abbrNr, abbreviation, normId, isFallback = false }: PageProps) {
  return (
    <AppPage
      style={{ paddingTop: 0 }}
    >
      {/* <Canonical /> */}
      {header}
      <Clamp clamp='1150px' style={{ padding: '0px 24px' }}>
        <Columns
          style={{ position: 'relative', minHeight: '70vh', contain: 'style' /* Don't do `contain: content` because otherwise the bottom bar won't show up on mobile... */ }}
          xAlign='start'
          gap={2.75}
          ratio='0.5/1'
          collapseAt='712px'
        >
          <Suspense fallback={<SidebarFallback />}>
            <Sidebar
              title={title}
              abbreviation={abbreviation}
              {...sidebar}
              normId={normId}
              showSearch={search}
              abbrNr={abbrNr}
              menu={menu}
              noToc={!tocContent}
            >
              {tocContent ?? infoContent ?? <></>}
            </Sidebar>
          </Suspense>
          <Suspense fallback={<ContentFallback />}>
            {isFallback
              ? (
                // @ts-expect-error Content could be a JSX element which would break the app...
                <MainLayoutFallback mainLayout={Content} />
                )
              : (isValidElement(Content)
                  ? Content
                  : <Content normId={normId} />
                )
              }
          </Suspense>
        </Columns>
      </Clamp>
      <InfoOverlay title={title}>
        {infoContent}
      </InfoOverlay>
      <BottomRight>
        <ScrollToTopButton />
      </BottomRight>
    </AppPage>
  )
}

export interface SidebarProps {
  title: string
  children: ReactElement<any>
  menu?: ReactNode
  style?: CSSProperties
  normId: string
  abbrNr?: number
  showSearch?: boolean
  headerClassName?: string
  sidebarClassName?: string
  headerButtons?: ReactElement<any>
  noToc?: boolean
  abbreviation?: string
}
function Sidebar ({ style = {}, noToc, ...props }: SidebarProps) {
  if (!props.children) { return null }

  return (
    <div className={sidebarStyles.root} style={style}>
      <Mobile className={styles.navMenuWrapper}>
        <BottomBar {...props} noToc={noToc} />
      </Mobile>
      <Desktop>
        <ClientSidebar {...props} noToc={noToc} />
      </Desktop>
    </div>
  )
}
export const Header = HeaderClient
export const Content = ContentClient

function SidebarFallback () {
  return (
    <Stack style={{ flexGrow: 0.5, paddingTop: 60, minWidth: 'min(17em, 100%)', maxWidth: '20em' }}>
      <Placeholder type='text' />
      <Placeholder type='text' />
      <Placeholder type='text' />
    </Stack>
  )
}

export function ContentFallback () {
  return (
    <Stack style={{ paddingTop: 60 }}>
      <Placeholder type='text' />
      <Placeholder type='text' />
      <Placeholder type='text' />
    </Stack>
  )
}
